import difference from 'lodash/difference';

const GSM_STANDARD_CHARS = [
  '@',
  '0',
  '¡',
  'P',
  '¿',
  'p',
  '£',
  '_',
  '!',
  '1',
  'A',
  'Q',
  'a',
  'q',
  '$',
  '"',
  '2',
  'B',
  'R',
  'b',
  'r',
  '¥',
  '?',
  '#',
  '3',
  'C',
  'S',
  'c',
  's',
  'è',
  '?',
  '4',
  'D',
  'T',
  'd',
  't',
  'é',
  '?',
  '%',
  '5',
  'E',
  'U',
  'e',
  'u',
  'ù',
  '6',
  'F',
  'V',
  'f',
  'v',
  'ì',
  '?',
  "'",
  '7',
  'G',
  'W',
  'g',
  'w',
  'ò',
  '(',
  '8',
  'H',
  'X',
  'h',
  'x',
  'Ç',
  ')',
  '9',
  'I',
  'Y',
  'i',
  'y',
  '*',
  ':',
  'J',
  'Z',
  'j',
  'z',
  'Ø',
  '+',
  ';',
  'K',
  'Ä',
  'k',
  'ä',
  'Æ',
  ',',
  '<',
  'L',
  'l',
  'ö',
  'æ',
  '-',
  '=',
  'M',
  'Ñ',
  'm',
  'ñ',
  'Å',
  'ß',
  '.',
  '>',
  'N',
  'Ü',
  'n',
  'ü',
  'å',
  'É',
  '/',
  'O',
  '§',
  'o',
  'à',
  'Δ',
  'Φ',
  'Γ',
  'Λ',
  'Ω',
  'Π',
  'Ψ',
  'Σ',
  'Θ',
  'Ξ',
  'Ö',
  'Ä',
  'Ü',
  'ø',
  ' ',
  '\r',
  '\n',
  '¤',
  '&',
];

const GSM_EXTENDED_CHARS = ['|', '^', '€', '{', '}', '[', ']', '~', '\\'];

const BYTE_SIZE = 8;
const CHARACTER_SIZE = 7;
const GSM_METADATA_LENGTH = 7;
const GSM_METADATA_SIZE = GSM_METADATA_LENGTH * CHARACTER_SIZE;
const GSM_MAX_SIZE = 140 * BYTE_SIZE;

function getTextSize(text: string, charSize: (ch: string) => number) {
  let totalSize = 0;
  let i = 0;

  while (i < text.length) {
    if (text[i] === '\\' && text[i + 1] === 'r') {
      totalSize += charSize('\r');
      i += 2;
    } else if (text[i] === '\\' && text[i + 1] === 'n') {
      totalSize += charSize('\n');
      i += 2;
    } else if (text[i] === '%' && text[i + 1] === '0' && text[i + 2] && /^[0-9A-Fa-f]$/.test(text[i + 2])) {
      totalSize += charSize('\n');
      i += 3;
    } else {
      totalSize += charSize(text[i]);
      i += 1;
    }
  }
  return totalSize;
}

export function getCharacterCount(textArray: string[]) {
  let totalSize = 0;
  for (let i = 0; i < textArray.length; i += 1) {
    const text = textArray[i];
    totalSize += getTextSize(text, () => 1);
  }
  return totalSize;
}

class CharacterCounter {
  text: string;

  constructor(text: string) {
    this.text = text;
  }

  getInfo() {
    const messages = this.getMessages();
    return {
      messages,
      unicodeRequired: this.isUnicodeRequired(),
      charactersCount: getCharacterCount(messages),
    };
  }

  isUnicodeRequired() {
    const remainder = difference([...this.text], [...GSM_STANDARD_CHARS, ...GSM_EXTENDED_CHARS]);
    return remainder.length !== 0;
  }

  getMessages() {
    return this.splitIntoSMS(GSM_MAX_SIZE);
  }

  splitIntoSMS(maxSize: number) {
    let index = 0;
    const messages = [];
    let { text } = this;
    let split = getTextSize(text, ch => this.getCharSize(ch)) > maxSize;
    while (index <= this.text.length) {
      const sms = this.getSMS(text, maxSize, split);
      messages.push(sms);
      text = text.slice(sms.length);
      index += sms.length + 1;
      split = getTextSize(text, ch => this.getCharSize(ch)) + GSM_METADATA_SIZE > maxSize;
    }
    return messages;
  }

  getSMS(text: string, maxSize: number, lastSplit: boolean) {
    let index = 0;
    let currentSize = 0;
    const maxSizeWithMeta = maxSize - GSM_METADATA_SIZE;

    while (currentSize + this.getCharSize(text[index]) <= maxSizeWithMeta && index < text.length) {
      currentSize += this.getCharSize(text[index]);
      index += 1;
    }
    if (!lastSplit) {
      index = text.length;
    }
    return text.slice(0, index);
  }

  getSizeInBytes() {
    return Math.ceil(getTextSize(this.text, ch => this.getCharSize(ch)) / BYTE_SIZE);
  }

  getCharSize(character: string) {
    if (!character) {
      return 0;
    }
    if (this.isUnicodeRequired()) {
      return character.length * BYTE_SIZE * 2;
    }
    if (GSM_EXTENDED_CHARS.includes(character)) {
      return CHARACTER_SIZE * 2;
    }
    return CHARACTER_SIZE;
  }
}

export default CharacterCounter;
